import React from 'react';
import { Container } from 'components/atoms';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

export type ChartType = 'bar' | 'line' | 'pie' | 'donut';

interface IChartData {
  name: string;
  value: number;
  [key: string]: any;
}

interface ISpotifyChartContainerProps {
  title: string;
  data: IChartData[];
  chartType: ChartType;
  loading?: boolean;
  className?: string;
  height?: number;
  xAxisKey?: string;
  yAxisKey?: string;
  color?: string;
  showLegend?: boolean;
  showTooltip?: boolean;
  formatValue?: (value: number) => string;
  customTooltip?: React.ComponentType<any>;
  customLegend?: React.ComponentType<any>;
  innerRadius?: number;
  outerRadius?: number;
  showGrid?: boolean;
  showReferenceLine?: boolean;
  referenceLineValue?: number;
}

const COLORS = ['#1DB954', '#FFFFFF', '#191414', '#B3B3B3', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];

const SpotifyChartContainer: React.FC<ISpotifyChartContainerProps> = ({
  title,
  data,
  chartType,
  loading = false,
  className = '',
  height = 300,
  xAxisKey = 'name',
  yAxisKey = 'value',
  color = 'var(--primary)',
  showLegend = true,
  showTooltip = true,
  formatValue = (value) => value.toLocaleString(),
  customTooltip,
  customLegend,
  innerRadius = 0,
  outerRadius = 100,
  showGrid = true,
  showReferenceLine = false,
  referenceLineValue = 0,
}) => {
  const DefaultTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="flex bg-opacity-95 rounded-sm p-3 gap-2"
          style={{ backgroundColor: 'var(--background)' }}
        >
          <span className="pl-4" style={{ color: 'var(--text)' }}>{label}</span>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color || entry.payload?.color || COLORS[index % COLORS.length] }}
              />
              <span className="pr-8" style={{ color: 'var(--text)' }}>
                {formatValue(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const DefaultLegend = ({ payload }: any) => {
    if (!showLegend || !payload) return null;
    
    return (
      <div className="flex flex-wrap gap-4 mt-4 justify-center">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm" style={{ color: 'var(--text)' }}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderChart = (): React.ReactElement | null => {
    const commonProps = {
      data,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {showGrid && (
              <CartesianGrid
                strokeDasharray=""
                vertical={false}
                opacity={0.5}
              />
            )}
            <XAxis dataKey={xAxisKey} stroke="var(--secondary)" />
            <YAxis
              width={80}
              stroke="var(--secondary)"
              tickFormatter={formatValue}
            />
            {showTooltip && (
              <Tooltip
                content={customTooltip || DefaultTooltip as any}
                cursor={{ fill: 'none' }}
              />
            )}
            {showReferenceLine && (
              <ReferenceLine y={referenceLineValue} stroke="var(--secondary)" />
            )}
            <Bar dataKey={yAxisKey} fill={color} />
            {showLegend && <Legend content={customLegend || DefaultLegend as any} />}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart {...commonProps}>
            {showGrid && (
              <CartesianGrid
                strokeDasharray=""
                vertical={false}
                opacity={0.5}
              />
            )}
            <XAxis dataKey={xAxisKey} stroke="var(--secondary)" />
            <YAxis
              width={80}
              stroke="var(--secondary)"
              tickFormatter={formatValue}
            />
            {showTooltip && (
              <Tooltip
                content={customTooltip || DefaultTooltip as any}
                cursor={{ fill: 'none' }}
              />
            )}
            {showReferenceLine && (
              <ReferenceLine y={referenceLineValue} stroke="var(--secondary)" />
            )}
            <Line
              type="monotone"
              dataKey={yAxisKey}
              stroke={color}
              activeDot={{ r: 6 }}
            />
            {showLegend && <Legend content={customLegend || DefaultLegend as any} />}
          </LineChart>
        );

      case 'pie':
      case 'donut':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={chartType === 'donut' ? innerRadius : 0}
              outerRadius={outerRadius}
              paddingAngle={2}
              dataKey={yAxisKey}
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color || COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            {showTooltip && (
              <Tooltip content={(customTooltip || DefaultTooltip) as any} />
            )}
            {showLegend && <Legend content={customLegend || DefaultLegend as any} />}
          </PieChart>
        );

      default:
        return null;
    }
  };

  return (
    <Container className={className}>
      <Container.Header title={title} />
      <Container.Content loading={loading}>
        <ResponsiveContainer width="100%" height={height}>
          {renderChart() || <div>Unsupported chart type</div>}
        </ResponsiveContainer>
      </Container.Content>
    </Container>
  );
};

export default SpotifyChartContainer;
